import { useMemo } from 'react';
import type { Field, FormField } from '@elfsight/widget-form-builder/widget';
import type {
  ChoiceType,
  DropdownType,
  ImageChoiceType,
  NumberType,
  SliderType
} from '../types';

export const useFieldsValues = (
  fields: FormField[],
  changedFields: Record<string, Field>
) => {
  const mapOptions = (options: { id: string; value: string | number }[] = []) =>
    options.map(({ id, value }) => ({
      id,
      value
    }));

  return useMemo(() => {
    return fields.flatMap<
      Record<string, string | string[] | number | undefined>
    >((field) => {
      const id = field.id;
      const changedField = changedFields[field.id];

      let value;

      if (field.type === 'choice' || field.type === 'imageChoice') {
        const typedField = field as ChoiceType | ImageChoiceType;

        value = typedField.multiple
          ? typedField.initialValueMultiple
          : typedField.initialValue;

        if (changedField?.valid && changedField?.value) {
          value = changedField.value as string | string[];
        }

        return [{ id, value }, ...mapOptions(typedField.options)];
      }

      if (field.type === 'dropdown') {
        const typedField = field as DropdownType;

        value = typedField.initialValue;
        if (changedField?.valid && changedField?.value) {
          value = changedField.value as string | string[];
        }

        return [{ id, value }, ...mapOptions(typedField.options)];
      }

      if (field.type === 'number' || field.type === 'slider') {
        const typedField = field as NumberType | SliderType;

        value = typedField.initialValue;

        if (changedField) {
          value = changedField?.valid ? (changedField.value as number) : NaN;
        }

        return { id, value };
      }

      return { id, value: undefined };
    });
  }, [fields, changedFields]);
};
